import { useState, useEffect, useMemo } from "react";
import AgGridComponent from "../AG-grid/AgGridComponent";
import LoadableComp from "../commonComponents/LoadableComp/LoadableComp";
import { getAnnouncements } from "./utils/methods";
import dayjsTZ from "../../utils/dayjs";
import "./Announcements.scss";
import AnnouncementReplies from "./AnnouncementReplies/AnnouncementReplies";
import NewAnnouncement from "./NewAnnouncement/NewAnnouncement";
import MondayButton from "../commonComponents/MondayButton/MondayButton";
import { useMediaQuery } from "react-responsive";
import { colDefs as columnDefs } from "./colDefs";
import { useSelector } from "react-redux";

export const categories = {
  importantAnnouncement: "Lajmërim",
  needAction: "Lajmërim që ka nevojë për përgjigje",
  otherWish: "Urim",
};
const cols = [
  {
    field: "confirm",
    headerName: "Konfirmoi",
    backgroundColor: "#1da193",
  },
  {
    field: "skip",
    headerName: "Anashkaloi",
    backgroundColor: "#e6951e",
  },
  {
    field: "refuse",
    headerName: "Refuzoi",
    backgroundColor: "#e74646",
  },
];
const FILTER_KEYS = [
  { key: "createdBy", label: "Krijuar nga", type: "string" },
  { key: "announcementTitle", label: "Titulli", type: "string" },
  {
    key: "announcementCategory",
    label: "Kategoria",
    type: "string",
    filterType: "equals",
  },
  {
    key: "createdAt",
    label: "Koha e krijimit",
    type: "date-range",
  },
];
const ITEMS = {
  search: {},
  icons: {
    filter: {
      filterKeys: FILTER_KEYS,
      replaceable: {
        announcementCategory: Object.entries(categories).map(([_, val]) => ({
          [val]: val,
        })),
      },
    },
    excel: {},
    pdf: {},
    print: {},
    delete: { endpoint: "announcements" },
  },
};

//region COMPONENT
function Announcements() {
  const { isDarkMode } = useSelector((state) => state.usersList);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReplies, setOpenReplies] = useState(false);

  const retrievedData = async () => {
    await getAnnouncements().then((r) => {
      const newRowData = (r || [])
        .map((el) => {
          const groupedResponses = Object.entries(
            el?.usersResponses || {}
          ).reduce((acc, [userId, obj]) => {
            const { response } = obj;
            if (!acc[response]) {
              acc[response] = {};
            }
            acc[response][userId] = obj.updatedAt;
            return acc;
          }, {});

          const toReturn = { ...(el || {}), responses: {}, groupedResponses };

          Object.values(el?.usersResponses || {}).forEach(
            ({ response: res = "" }) => {
              if (!!res) {
                toReturn.responses = {
                  ...(toReturn.responses || {}),
                  [res]: (toReturn.responses[res] || 0) + 1,
                };
              }
            }
          );
          return toReturn;
        })
        .sort((a, b) => dayjsTZ(b.createdAt).diff(dayjsTZ(a.createdAt)));

      setRowData(newRowData);
      setLoading(false);
    });
  };

  // region MODIFYROWSTOEXP
  const modifyRowsToExport = (rows) => {
    if (!rows?.length) return [];
    let modifiedRows = [];
    rows?.forEach((row) => {
      const structuredResponses = [];
      for (const [key, obj] of Object.entries(row?.usersResponses || [])) {
        structuredResponses.push({ key: key, ...obj });
      }
      let userResponses = Object.groupBy(
        structuredResponses,
        ({ response }) => response
      );
      const responses = [];
      for (const [res, arr] of Object.entries(userResponses)) {
        responses.push(`${res}: ${arr?.length}`);
      }
      const toReturn = {
        ...row,
        announcementCategory: categories[row?.announcementCategory],
        usersResponses: !!responses?.length ? responses : 0,
      };
      modifiedRows.push(toReturn);
    });
    return modifiedRows;
  };

  useEffect(() => {
    retrievedData();
  }, []);

  const isMobileView = useMediaQuery({
    query: "(max-width: 450px)",
  });

  const colDefs = useMemo(() => {
    const customCols = cols.map(({ headerName, field, backgroundColor }) => ({
      headerName,
      field: `groupedResponses.${field}`,
      width: 75,
      valueGetter: ({ data }) =>
        data.groupedResponses?.[field]
          ? Object.keys(data.groupedResponses[field]).length
          : "",
      cellRenderer: ({ value, node, data }) => {
        if (node.group || !value) return null;

        const userResponses = data.groupedResponses?.[field] || {};
        const urLength = Object.keys(userResponses).length || 0;

        return (
          <div className="userResponses">
            <span
              style={{
                color: "#fff",
                backgroundColor,
                cursor: urLength > 0 ? "pointer" : "not-allowed",
              }}
              onClick={
                urLength > 0
                  ? () => setOpenReplies({ record: data, field })
                  : null
              }
            >
              {urLength}
            </span>
          </div>
        );
      },
    }));

    return columnDefs({
      customCols,
      categories,
      isMobileView,
      setOpenReplies,
      setOpenEditModal,
    });
  }, [isMobileView, isDarkMode]);

  //region RETURN
  return (
    <LoadableComp loading={loading}>
      <div className="globalPageWrapper">
        <div className="announcementsWrapper">
          <AgGridComponent
            gridApi={gridApi}
            rowData={rowData}
            rowSelection={{ mode: "multiRow" }}
            onGridReady={(p) => setGridApi(p.api)}
            columnDefs={colDefs}
            paginator={true}
            defaultExcelExportParams={{
              processCellCallback: (params) =>
                params.column.getColId() === "usersResponses"
                  ? null
                  : params.value,
              processHeaderCallback: (params) =>
                params.column.getColId() === "usersResponses"
                  ? null
                  : params.column.getColDef().headerName,
            }}
            headerProps={{
              items: ITEMS,
              exportTitle: "Lista e Lajmërimeve",
              modifyRows: modifyRowsToExport,
              children: [
                <MondayButton
                  className="mondayButtonGreen"
                  onClick={() => setOpenEditModal(true)}
                >
                  Lajmërim i Ri
                </MondayButton>,
              ],
            }}
            idKey={"announcementId"}
            onRowClick={(_, row) => setOpenEditModal(row)}
          />
        </div>
      </div>
      {!!openEditModal ? (
        <NewAnnouncement
          {...{
            ...(typeof openEditModal !== "boolean"
              ? { data: openEditModal }
              : {}),
            gridApi,
            onReset: () => {},
            open: !!openEditModal,
            setOpen: setOpenEditModal,
            retrievedData,
          }}
        />
      ) : null}
      {openReplies ? (
        <AnnouncementReplies
          open={!!openReplies}
          setOpen={setOpenReplies}
          filterBy={openReplies.field}
          announcement={openReplies.record}
        />
      ) : null}
    </LoadableComp>
  );
}

export default Announcements;

export const data = [
  [
    "Lloji",
    "Numri i pergjigjeve",
    { role: "style" },
    {
      sourceColumn: 0,
      role: "annotation",
      type: "string",
      calc: "stringify",
    },
  ],
  ["Konfirmoi", 8.94, "#b87333", null],
  ["Anashkaloi", 10.49, "silver", null],
  ["Refuzoi", 19.3, "gold", null],
];
